import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { EventCard } from "../components/EventCard";
import { CATEGORIES, STATES } from "../lib/categories";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Phone, X } from "lucide-react";

const FILTERS = [{ value: "all", label: "All Types" }, ...CATEGORIES.map((c) => ({ value: c.value, label: c.short }))];
const STATE_FILTERS = [{ value: "all", label: "All States" }, ...STATES.map((s) => ({ value: s.value, label: s.abbr }))];

export default function Dashboard() {
  const { user, updateProfile } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [mineOnly, setMineOnly] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [showPhone, setShowPhone] = useState(false);
  const [phone, setPhone] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/events");
      setEvents(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (user && !user.phone) setShowPhone(true);
  }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    return events.filter((e) => {
      if (filter !== "all" && e.category !== filter) return false;
      if (stateFilter !== "all" && e.state !== stateFilter) return false;
      if (mineOnly && !e.mine) return false;
      return true;
    });
  }, [events, filter, stateFilter, mineOnly]);

  const stats = useMemo(() => {
    const open = events.filter((e) => !e.is_booked).length;
    const mine = events.filter((e) => e.mine).length;
    return { total: events.length, open, mine };
  }, [events]);

  const book = async (ev) => {
    setBusyId(ev.id);
    try {
      const { data } = await api.post(`/events/${ev.id}/book`);
      setEvents((prev) => prev.map((e) => (e.id === ev.id ? data : e)));
      toast.success("Event claimed. It's yours.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      load();
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (ev) => {
    setBusyId(ev.id);
    try {
      const { data } = await api.post(`/events/${ev.id}/cancel`);
      setEvents((prev) => prev.map((e) => (e.id === ev.id ? data : e)));
      toast.success("Booking canceled.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const savePhone = async () => {
    try {
      await updateProfile({ phone });
      toast.success("Number saved — reminders are on.");
      setShowPhone(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] text-white flex flex-col">
      <Navbar />

      {/* Header */}
      <section className="border-b border-white/10 grid-lines">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-12">
          <div className="overline text-[hsl(var(--primary))] mb-3">The Schedule</div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight leading-none mb-8">
            EVENTS FOR REGION 4
          </h1>
          <div className="flex flex-wrap gap-px bg-white/10 border border-white/10 w-fit">
            {[
              { k: "total", label: "Total Events", v: stats.total },
              { k: "open", label: "Open Events", v: stats.open },
              { k: "mine", label: "Booked By You", v: stats.mine },
            ].map((s) => (
              <div key={s.k} data-testid={`stat-${s.k}`} className="bg-[#22303C] px-8 py-5">
                <div className="font-display text-4xl leading-none">{s.v}</div>
                <div className="overline text-white/40 mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filters */}
      <div className="border-b border-white/10 bg-[#1A1A1A] sticky top-16 z-40">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="overline text-white/40 mr-1 w-14">State</span>
            {STATE_FILTERS.map((f) => (
              <button
                key={f.value}
                data-testid={`state-filter-${f.value}`}
                onClick={() => setStateFilter(f.value)}
                className={`overline px-4 py-2 border transition-colors ${
                  stateFilter === f.value
                    ? "bg-white text-black border-white"
                    : "border-white/15 text-white/60 hover:text-white hover:border-white/40"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="overline text-white/40 mr-1 w-14">Type</span>
            {FILTERS.map((f) => (
              <button
                key={f.value}
                data-testid={`filter-${f.value}`}
                onClick={() => setFilter(f.value)}
                className={`overline px-4 py-2 border transition-colors ${
                  filter === f.value
                    ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
                    : "border-white/15 text-white/60 hover:text-white hover:border-white/40"
                }`}
              >
                {f.label}
              </button>
            ))}
            <div className="ml-auto">
              <button
                data-testid="filter-mine"
                onClick={() => setMineOnly((v) => !v)}
                className={`overline px-4 py-2 border transition-colors ${
                  mineOnly
                    ? "bg-white text-black border-white"
                    : "border-white/15 text-white/60 hover:text-white hover:border-white/40"
                }`}
              >
                My Bookings
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Grid */}
      <main className="flex-1 max-w-[1400px] w-full mx-auto px-5 sm:px-8 py-10">
        {loading ? (
          <div className="py-32 text-center font-display text-3xl text-white/30 animate-pulse">
            LOADING SCHEDULE…
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="empty-state" className="py-32 text-center">
            <div className="font-display text-4xl text-white/30 mb-3">NO EVENTS HERE</div>
            <p className="text-white/40">
              {mineOnly ? "You haven't booked anything yet." : "Check back soon — the admin is loading the schedule."}
            </p>
          </div>
        ) : (
          <div className="grid gap-px bg-white/10 border border-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((ev, i) => (
              <EventCard
                key={ev.id}
                event={ev}
                index={i}
                busy={busyId === ev.id}
                onBook={book}
                onCancel={cancel}
              />
            ))}
          </div>
        )}
      </main>

      {/* Phone prompt */}
      {showPhone && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md bg-[#22303C] border border-white/15 p-8"
          >
            <div className="flex items-start justify-between mb-4">
              <Phone className="w-8 h-8 text-[hsl(var(--primary))]" />
              <button data-testid="close-phone" onClick={() => setShowPhone(false)} className="text-white/40 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <h3 className="font-display text-3xl tracking-tight mb-2">GET TEXT REMINDERS</h3>
            <p className="text-white/50 text-sm mb-5 leading-relaxed">
              Add your mobile number to get a text 2 days out, 1 day out, and the morning of every
              event you book.
            </p>
            <input
              data-testid="phone-input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 512 555 0134"
              className="w-full bg-[#1A1A1A] border border-white/15 px-4 py-3 text-white mb-4 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
            />
            <button
              data-testid="save-phone"
              onClick={savePhone}
              className="w-full bg-[hsl(var(--primary))] text-white overline py-3 hard-shadow-hover"
            >
              Save Number
            </button>
          </motion.div>
        </div>
      )}

      <Footer />
    </div>
  );
}
